import Backbone from 'backbone';
import $ from 'jquery';
import React from 'react';

import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import EnzymeList, {EnzymeListCollection} from 'molbio/enzymes/backbone/EnzymeList';
import Enzymes, {Enzyme} from 'molbio/enzymes/backbone/Enzymes';
import EnzymeListEditor from 'molbio/enzymes/EnzymeListEditor';
import EnzymeListRowTemplate, {EnzymeListRowProps} from 'molbio/enzymes/EnzymeListRowTemplate';
import EnzymeRowTemplate, {EnzymeRowProps} from 'molbio/enzymes/EnzymeRowTemplate';
import notifications from 'notifications';
import OwnablesManageView, {OwnablesManageViewOptions} from 'profile/OwnablesManageView';
import {renderToDOM} from 'redux-stores/ReduxDOMRender';
import {BasicEvent} from 'util/Types';

interface EnzymeListRowOptions extends Backbone.ViewOptions<Enzyme> {
  writable: boolean;
}

class EnzymeListRow extends Backbone.View<Enzyme> {
  writable!: boolean;
  template!: (args?: EnzymeListRowProps) => string;

  static initClass(): void {
    this.prototype.tagName = 'tr';
    this.prototype.className = 'enzyme-row';
    this.prototype.template = EnzymeListRowTemplate;
    // @ts-expect-error: Backbone events can be either the events hash or a method returning the events hash
    // The Backbone types do not know about this.
    this.prototype.events = {'click .close': 'unselect'};
  }

  override initialize(options: EnzymeListRowOptions): void {
    assert(options.writable != null, 'writable is required');
    this.writable = options.writable;
  }

  die(): void {
    this.stopListening();
    this.undelegateEvents();
  }

  unselect(): void {
    this.model.set({active: false});
  }

  override render(): EnzymeListRow {
    this.$el.html(this.template({enzyme: this.model, writable: this.writable}));
    return this;
  }
}
EnzymeListRow.initClass();

class CommonEnzymeRowView extends Backbone.View<Enzyme> {
  template!: (args?: EnzymeRowProps) => string;

  static initClass(): void {
    this.prototype.tagName = 'tr';
    this.prototype.className = 'enzyme-row';
    this.prototype.template = EnzymeRowTemplate;
    // @ts-expect-error: Backbone events can be either the events hash or a method returning the events hash
    // The Backbone types do not know about this.
    this.prototype.events = {click: 'selectEnzyme'};
  }

  override initialize(): void {
    this.listenTo(this.model, 'change:active', () => this.render());
    return this.listenTo(this.model, 'change:catalogVisible', () => this.render());
  }

  selectEnzyme(): void {
    this.model.set({active: !this.model.get('active')});
  }

  die(): void {
    this.stopListening();
    this.undelegateEvents();
  }

  override render(): CommonEnzymeRowView {
    if (!this.model.get('catalogVisible')) {
      this.$el.addClass('hidden');
      return this;
    }
    this.$el.removeClass('hidden');
    this.$el.html(this.template({enzyme: this.model}));
    if (this.model.get('active')) {
      this.$el.addClass('active');
    } else {
      this.$el.removeClass('active');
    }
    return this;
  }
}
CommonEnzymeRowView.initClass();

// @ts-expect-error: This view uses a collection as a model, which is not supported by Backbone types
interface EnzymeListContentViewOptions extends Backbone.ViewOptions<Enzymes> {
  enzymes?: Enzymes;
}

// @ts-expect-error: This view uses a collection as a model, which is not supported by Backbone types
class EnzymeListContentView extends Backbone.View<Enzymes> {
  itemViews: Array<EnzymeListRow> | null = null;
  enzymes!: Enzymes;
  list!: EnzymeList;

  static initClass(): void {
    this.prototype.el = '.enzyme-list-table tbody';
  }

  override initialize(options: EnzymeListContentViewOptions): void {
    assert(options.enzymes != null, 'enzymes is required');
    this.enzymes = options.enzymes;
    this.listenTo(this.enzymes, 'change:active', () => this.render());
    this.listenTo(this.enzymes, 'inventoryChange', () => this.render());
  }

  update(list: EnzymeList): void {
    this.list = list;
  }

  die(): void {
    this.stopListening();
    this.undelegateEvents();
    if (this.itemViews != null) {
      for (const view of Array.from(this.itemViews)) {
        view.die();
      }
    }
  }

  override render(): EnzymeListContentView {
    if (this.itemViews != null) {
      for (const view of Array.from(this.itemViews)) {
        view.die();
      }
    }

    const models = this.enzymes.filter((enzyme) => enzyme.get('active'));
    const {writable} = this.list.get('permissions');
    this.itemViews = models.map((enzyme) =>
      new EnzymeListRow({
        model: enzyme,
        writable,
      } as EnzymeListRowOptions).render()
    );
    const els: Array<JQuery> = this.itemViews.map((view) => view.el);
    if (els.length > 0) {
      // @ts-expect-error You can, but should not, pass JQuery objects to `JQuery.html`
      this.$el.html(els);
    } else {
      let help;
      if (writable) {
        help = $('<p>').html('<em>This list is empty - add enzymes by clicking on them from the right.</em>');
      } else {
        help = $('<p>').html('<em>This list is empty.</em>');
      }
      // @ts-expect-error You can, but should not, pass JQuery objects to `JQuery.html`
      this.$el.html(help);
    }
    return this;
  }
}
EnzymeListContentView.initClass();

// @ts-expect-error: This view uses a collection as a model, which is not supported by Backbone types
class CommonEnzymesView extends Backbone.View<Enzymes> {
  itemViews: Array<CommonEnzymeRowView> | null = null;

  static initClass(): void {
    this.prototype.el = '.common-enzymes-table tbody';
  }

  die(): void {
    this.stopListening();
    this.undelegateEvents();
    if (this.itemViews != null) {
      for (const view of Array.from(this.itemViews)) {
        view.die();
      }
    }
  }

  override render(): CommonEnzymesView {
    if (this.itemViews != null) {
      for (const view of Array.from(this.itemViews)) {
        view.die();
      }
    }
    this.itemViews = this.model.map((enzyme) => new CommonEnzymeRowView({model: enzyme}).render());
    const els: Array<JQuery> = this.itemViews.map((view) => view.el);
    // @ts-expect-error You can, but should not, pass JQuery objects to `JQuery.html`
    this.$el.html(els);
    return this;
  }
}
CommonEnzymesView.initClass();

let enzymeListsCollection: EnzymeListCollection | null = null;

class EnzymeListsView extends OwnablesManageView<EnzymeList> {
  type!: string;
  modelType!: string;
  enzymes!: Enzymes;
  enzymeContent!: EnzymeListContentView;
  commonEnzymes!: CommonEnzymesView;
  ownables!: EnzymeListCollection;

  static initClass(): void {
    this.prototype.el = '.js-enzyme-lists';
    this.prototype.type = 'enzyme-lists';
    this.prototype.modelType = ModelTypeConstants.ENZYME_LIST;
  }

  override events(): Backbone.EventsHash {
    return {
      ...super.events(),
      'change .search': 'filter',
      'keyup .search': 'filter',
      'submit .create-new form': 'createNewList',
      'click .save-enzymes': 'saveEnzymes',
    };
  }

  static getBootstrappedOwnables(): EnzymeListCollection {
    if (enzymeListsCollection == null) {
      enzymeListsCollection = new EnzymeListCollection(window.bootstrap!.enzyme_lists, {parse: true});
    }
    return enzymeListsCollection;
  }

  override initialize(options: OwnablesManageViewOptions<EnzymeList>): void {
    this.enzymes = Enzymes.getCommonEnzymes();
    this.enzymeContent = new EnzymeListContentView({enzymes: this.enzymes} as EnzymeListContentViewOptions);
    this.commonEnzymes = new CommonEnzymesView({model: this.enzymes});
    super.initialize(options);
  }

  override die(): void {
    super.die();
    this.enzymeContent.die();
    this.commonEnzymes.die();
  }

  saveEnzymes(): void {
    this.$('.save-enzymes').addClass('disabled');
    this.currentOwnable
      .save(
        {members: Array.from(this.enzymes.where({active: true})).map((enzyme) => enzyme.id)},
        {patch: true, url: `${this.currentOwnable.url()}/attributes`}
      )
      .always(() => {
        this.$('.save-enzymes').removeClass('disabled');
      })
      .done(() => {
        notifications.success('Enzyme list successfully updated.');
      })
      .fail(notifications.notifyError);
  }

  saveEnzymesForReact(selectedEnzymeIds: Array<number>): Promise<void> {
    return new Promise((resolve) => {
      this.currentOwnable
        .save({members: selectedEnzymeIds}, {patch: true, url: `${this.currentOwnable.url()}/attributes`})
        .done(() => {
          notifications.success('Enzyme list successfully updated.');
          resolve();
        })
        .fail((error: Response) => {
          notifications.notifyError(error);
          // We have already notified the user of the error, so we resolve here.
          resolve();
        });
    });
  }

  filter(): void {
    const parts: Array<string> = this.$el.find('.search').val().split(' ');
    this.enzymes.each((enzyme) => {
      let searchVisible = true;
      const name = enzyme.get('name').toLowerCase();
      for (const part of parts) {
        searchVisible = searchVisible && name.indexOf(part.toLowerCase()) >= 0;
      }

      enzyme.set('catalogVisible', searchVisible);
    });
  }

  createNewList(e: BasicEvent): void {
    e.preventDefault();
    const list = new EnzymeList({
      name: this.$el.find('.create-new .name').val(),
      description: this.$el.find('.create-new .description').val(),
      owner_id: window.bootstrap!.current_entity_id,
    });
    list
      .save()
      .done(() => {
        this.$el.find('.create-new .name, .create-new .description').val('');
        this.ownables.add(list);
        this.navigateToOwnable(list.id);
        notifications.success('List created successfully!');
        analytics.track('DATA_MANAGEMENT', 'created_ownable', {
          type: this.type,
          name: list.get('name'),
          has_description: list.get('description').length > 0,
        });
      })
      .fail(notifications.notifyError);
  }

  override renderOwnable(): void {
    super.renderOwnable();
    const list = this.currentOwnable;
    const {writable} = list.get('permissions');

    if (BenchlingConfig.MOLBIO_REACT_ENZYME_LISTS_VIEW) {
      this.renderReactContent(list, writable);
    } else {
      this.renderBackboneContent(list, writable);
    }

    $.getJSON(`/1/api/enzyme-lists/${list.id}/collaborators/`)
      .done((res) => {
        this.createCollaboratorsView(res);
      })
      .fail(notifications.notifyError);
  }

  private renderReactContent(list: EnzymeList, writable: boolean): void {
    // Hide the Backbone view elements
    this.$el.find('.enzyme-list-backbone-view').addClass('hidden');

    const selectedEnzymeIds: Array<number> = list.get('members').map((enzyme) => enzyme.id);
    const enzymes = this.enzymes.models.map((enzyme) => ({
      id: enzyme.id as number,
      name: enzyme.get('name'),
    }));

    renderToDOM(
      <EnzymeListEditor
        key={list.id}
        enzymes={enzymes}
        selectedEnzymeIds={selectedEnzymeIds}
        isReadOnly={!writable}
        onSave={async (newSelectedIds) => {
          await this.saveEnzymesForReact(newSelectedIds);
        }}
      />,
      this.el.querySelector('.enzyme-list-editor')
    );
  }

  private renderBackboneContent(list: EnzymeList, writable: boolean): void {
    // Show the Backbone view elements
    this.$el.find('.enzyme-list-backbone-view').removeClass('hidden');

    this.enzymeContent.update(list);
    const enzymeIds: Array<number> = list.get('members').map((enzyme) => enzyme.id);
    for (const enzyme of this.enzymes.models) {
      enzyme.set({active: enzymeIds.includes(enzyme.id as number)}, {silent: true});
    }
    this.enzymes.trigger('inventoryChange');

    this.commonEnzymes.render();
    this.$el.find('.common-enzymes-disabled').toggleClass('hidden', writable);
    this.$el.find('.save-enzymes').toggleClass('hidden', !writable);
  }
}
EnzymeListsView.initClass();

export default EnzymeListsView;
